import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Spin,
  Tooltip,
  Empty,
  Descriptions,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  RobotOutlined,
  ArrowRightOutlined,
  ReloadOutlined,
  FilterOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ScreeningTask, Candidate } from '../../types/scout';
import type { Proposal } from '../../types/proposal';
import { scoutApi } from '../../services/scout';
import { proposalApi } from '../../services/proposal';
import { ROUTES } from '../../config/routes';

const { Title, Text } = Typography;
const { Option } = Select;

export const ScreeningTasks: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<ScreeningTask[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 提案列表（用于关联提案选择）
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);

  // 新建任务弹窗
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  // 任务详情相关
  const [selectedTask, setSelectedTask] = useState<ScreeningTask | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesPagination, setCandidatesPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 评估相关
  const [evaluatingCandidateId, setEvaluatingCandidateId] = useState<string>('');

  // 加载任务列表
  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await scoutApi.getScreeningTasks({
        page: pagination.current,
        page_size: pagination.pageSize,
      });
      setTasks(res.data || []);
      setPagination({
        ...pagination,
        total: res.pagination?.total || 0,
      });
    } catch (error) {
      message.error('加载筛选任务失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  // 加载提案列表
  const loadProposals = useCallback(async () => {
    setProposalsLoading(true);
    try {
      const res = await proposalApi.getProposals({ page: 1, page_size: 100 });
      setProposals(res.data || []);
    } catch (error) {
      // 静默失败，不影响主流程
    } finally {
      setProposalsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  // 创建任务
  const handleCreateTask = async () => {
    const values = createForm.getFieldsValue();
    setCreating(true);
    try {
      await scoutApi.createScreeningTask({
        name: values.name,
        proposal_id: values.proposal_id,
        search_source: values.search_source,
        search_params: {},
      });
      message.success('筛选任务创建成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      loadTasks();
    } catch (error) {
      message.error('创建任务失败');
    } finally {
      setCreating(false);
    }
  };

  // 查看任务详情
  const handleViewDetail = async (task: ScreeningTask) => {
    setSelectedTask(task);
    setDetailModalVisible(true);
    loadCandidates(task.id);
  };

  // 加载候选红人
  const loadCandidates = async (taskId: string, page = 1, pageSize = 10) => {
    setCandidatesLoading(true);
    try {
      const res = await scoutApi.getCandidates(taskId, {
        page,
        page_size: pageSize,
      });
      setCandidates(res.data || []);
      setCandidatesPagination({
        current: page,
        pageSize,
        total: res.pagination?.total || 0,
      });
    } catch (error) {
      message.error('加载候选红人失败');
    } finally {
      setCandidatesLoading(false);
    }
  };

  // 移除候选红人
  const handleRemoveCandidate = async (candidateId: string) => {
    if (!selectedTask) return;
    try {
      await scoutApi.removeCandidate(selectedTask.id, candidateId);
      message.success('已移除候选红人');
      loadCandidates(selectedTask.id, candidatesPagination.current, candidatesPagination.pageSize);
    } catch (error) {
      message.error('移除失败');
    }
  };

  // 批量评估
  const handleBatchEvaluate = async () => {
    if (!selectedTask || candidates.length === 0) return;
    message.info('开始批量评估...');

    for (const candidate of candidates) {
      try {
        await scoutApi.evaluateInfluencer(candidate.influencer_id, {
          influencer_id: candidate.influencer_id,
          candidate_list_id: selectedTask.id,
        });
      } catch (error) {
        console.error('评估失败:', candidate.influencer_id);
      }
    }

    message.success('批量评估任务已提交');
    // 刷新列表
    setTimeout(() => {
      loadCandidates(selectedTask.id, candidatesPagination.current, candidatesPagination.pageSize);
    }, 2000);
  };

  // 流转邀约
  const handleTransferToOutreach = (candidate: Candidate) => {
    if (!candidate.influencer) return;
    // 跳转到邀约页面，携带红人信息
    navigate(ROUTES.OUTREACH_CAMPAIGNS, {
      state: {
        influencerId: candidate.influencer_id,
        influencerName: candidate.influencer.name,
      },
    });
  };

  // 获取任务状态标签
  const getTaskStatusTag = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge status="processing" text="进行中" />;
      case 'paused':
        return <Badge status="warning" text="已暂停" />;
      case 'completed':
        return <Badge status="success" text="已完成" />;
      default:
        return <Badge status="default" text={status} />;
    }
  };

  // 获取评估状态标签
  const getEvaluationStatusTag = (status?: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="green" icon={<CheckCircleOutlined />}>已完成</Tag>;
      case 'evaluating':
      case 'pending':
      case 'processing':
        return <Tag color="orange" icon={<RobotOutlined />}>评估中</Tag>;
      case 'failed':
        return <Tag color="red" icon={<CloseCircleOutlined />}>失败</Tag>;
      default:
        return <Tag>未评估</Tag>;
    }
  };

  // 任务列表列定义
  const taskColumns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ScreeningTask) => (
        <a onClick={() => handleViewDetail(record)}>{text}</a>
      ),
    },
    {
      title: '提案',
      dataIndex: 'proposal_id',
      key: 'proposal_id',
      render: (proposalId: string) => {
        const proposal = proposals.find(p => p.id === proposalId);
        return proposal ? proposal.title : (proposalId || '-');
      },
    },
    {
      title: '红人数',
      dataIndex: 'influencer_count',
      key: 'influencer_count',
      render: (count: number) => count || 0,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getTaskStatusTag(status),
    },
    {
      title: '搜索来源',
      dataIndex: 'search_source',
      key: 'search_source',
      render: (source: string) => {
        const sourceMap: Record<string, string> = {
          database: '数据库搜索',
          competitor: '竞品采集',
          similar: '相似推荐',
        };
        return sourceMap[source] || source || '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: unknown, record: ScreeningTask) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 候选红人列表列定义
  const candidateColumns = [
    {
      title: '红人名称',
      dataIndex: 'influencer',
      key: 'influencer_name',
      render: (influencer: Candidate['influencer']) => (
        <a onClick={() => influencer && navigate(`/scout/influencers/${influencer}`)}>
          {influencer?.name || '-'}
        </a>
      ),
    },
    {
      title: '平台',
      dataIndex: 'influencer',
      key: 'platform',
      render: (influencer: Candidate['influencer']) => (
        <Tag>{influencer?.platform?.toUpperCase?.() || influencer?.platform || '-'}</Tag>
      ),
    },
    {
      title: '粉丝数',
      dataIndex: 'influencer',
      key: 'follower_count',
      render: (influencer: Candidate['influencer']) =>
        influencer?.follower_count?.toLocaleString() || '-',
    },
    {
      title: '国家',
      dataIndex: 'influencer',
      key: 'country',
      render: (influencer: Candidate['influencer']) => influencer?.country || '-',
    },
    {
      title: '匹配度',
      dataIndex: 'match_score',
      key: 'match_score',
      render: (score?: number) => {
        if (score === undefined) return '-';
        const color = score >= 80 ? 'green' : score >= 60 ? 'orange' : 'red';
        return <Tag color={color}>{score}%</Tag>;
      },
    },
    {
      title: '数据来源',
      dataIndex: 'data_source',
      key: 'data_source',
      render: (source: string) => {
        const sourceMap: Record<string, string> = {
          database: '数据库搜索',
          competitor: '竞品采集',
          similar: '相似推荐',
        };
        return sourceMap[source] || source || '-';
      },
    },
    {
      title: '评估状态',
      dataIndex: 'evaluation_status',
      key: 'evaluation_status',
      render: (status: string) => getEvaluationStatusTag(status),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: unknown, record: Candidate) => (
        <Space size="small">
          <Tooltip title="查看红人详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => record.influencer_id && navigate(`/scout/influencers/${record.influencer_id}`)}
            />
          </Tooltip>
          <Tooltip title="流转邀约">
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => handleTransferToOutreach(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确认移除"
            description="确定要将该红人从候选列表中移除吗？"
            onConfirm={() => handleRemoveCandidate(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Tooltip title="移除">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          筛选任务
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
        >
          新建任务
        </Button>
      </div>

      {/* 任务列表 */}
      <Card>
        <Table
          columns={taskColumns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize: pageSize || 10 });
            },
          }}
          locale={{ emptyText: '暂无筛选任务' }}
        />
      </Card>

      {/* 新建任务弹窗 */}
      <Modal
        title="新建筛选任务"
        open={createModalVisible}
        onOk={handleCreateTask}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        confirmLoading={creating}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="name"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="输入任务名称" />
          </Form.Item>
          <Form.Item
            name="proposal_id"
            label="关联提案"
          >
            <Select placeholder="选择关联提案（可选）" allowClear loading={proposalsLoading}>
              {proposals.map(p => (
                <Option key={p.id} value={p.id}>{p.title}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="search_source"
            label="搜索来源"
          >
            <Select placeholder="选择搜索来源（可选）" allowClear>
              <Option value="database">数据库搜索</Option>
              <Option value="competitor">竞品采集</Option>
              <Option value="similar">相似推荐</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 任务详情弹窗 */}
      <Modal
        title={selectedTask?.name}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedTask(null);
          setCandidates([]);
        }}
        width={1200}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        {selectedTask && (
          <div>
            {/* 任务基本信息 */}
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={24}>
                <Col span={6}>
                  <Statistic title="红人数" value={selectedTask.influencer_count} />
                </Col>
                <Col span={6}>
                  <Statistic title="状态" value={selectedTask.status} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="创建时间"
                    value={selectedTask.created_at ? new Date(selectedTask.created_at).toLocaleDateString('zh-CN') : '-'}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      icon={<RobotOutlined />}
                      onClick={handleBatchEvaluate}
                      style={{ marginRight: 8 }}
                    >
                      批量评估
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => loadCandidates(selectedTask.id, candidatesPagination.current, candidatesPagination.pageSize)}
                    >
                      刷新
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* 候选红人列表 */}
            <Card title="候选红人">
              <Table
                columns={candidateColumns}
                dataSource={candidates}
                rowKey="id"
                loading={candidatesLoading}
                pagination={{
                  current: candidatesPagination.current,
                  pageSize: candidatesPagination.pageSize,
                  total: candidatesPagination.total,
                  showSizeChanger: true,
                  showTotal: (total) => `共 ${total} 条`,
                  onChange: (page, pageSize) => {
                    loadCandidates(selectedTask.id, page, pageSize || 10);
                  },
                }}
                locale={{ emptyText: '暂无候选红人' }}
              />
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};
